import { useCallback, useRef, useState } from 'react';
import { Button, IconArrowLeft, IconArrowRight, useStyletron } from '@olxeu-jobs/ui';
import type { CompanyProfileCarouselProfileFragment } from '~api';
import { Link } from '~features/router';
import { useHostProps } from '~features/host-props';
import { Platform } from '~types';
import { Carousel, type CarouselHandle, type CarouselSlideSource } from '~ui/components';
import { useTracking } from '~features/tracking';
import { useFormatMessage } from '~i18n';
import { SrText } from '~ui/components';
import { EmployerCard } from '~features/employer-card';
import { EmployerCardLoader } from '~features/employer-card/employer-card-loader';
import {
  CarouselHeaderWrapper,
  CarouselWrapper,
  CarouselHeadingContainer,
  LinkParagraph,
  CarouselHeading,
  SkeletonWrapper,
} from './styled-components';

type Props = {
  profiles: CompanyProfileCarouselProfileFragment[] | undefined;
  heading: string;
  headingLevel: 2 | 3 | 4 | 5;
  loading: boolean;
  seeAllPath?: string;
  onSeeAllClick?: React.MouseEventHandler<HTMLAnchorElement>;
  target?: '_blank' | '_self';
  showPublicPath?: boolean;
};

const MIN_ITEM_COUNT_DESKTOP = 4;
const MIN_ITEM_COUNT_MOBILE = 1;

export const EmployersCarousel = ({
  profiles,
  heading,
  headingLevel,
  loading,
  seeAllPath,
  onSeeAllClick,
  target,
}: Props) => {
  const [, theme] = useStyletron();
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselRef = useRef<CarouselHandle>(null);
  const { trackEvent } = useTracking();
  const { platform, pathBuilder } = useHostProps();
  const formatMessage = useFormatMessage();
  const isMobile = platform === Platform.MOBILE;

  const onLinkClick = useCallback(
    () =>
      trackEvent('employer_profile_click', {
        touchPointButton: 'carousel_link',
      }),
    [trackEvent],
  );

  const items = profiles?.map(({ id, slug, ...profile }) => {
    const publicPath = pathBuilder.profilePublic({ uuidOrSlug: slug, bs: 'carousel_link' });

    return {
      key: id,
      node: (
        <EmployerCard
          {...profile}
          headingLevel={(headingLevel + 1) as 3 | 4 | 5 | 6}
          onLinkClick={onLinkClick}
          publicPath={publicPath}
          target={target}
        />
      ),
    };
  });
  const { slideCount = 0 } = carouselRef?.current || {};

  const onSlideChange = useCallback(
    ({
      currentIndex,
      prevIndex,
      slideSource,
    }: {
      currentIndex: number;
      prevIndex: number;
      slideSource: CarouselSlideSource;
    }) => {
      const indexDiff = currentIndex - prevIndex;
      const eventName = indexDiff > 0 ? 'company_carousel_next' : 'company_carousel_previous';

      if (slideSource === 'dot') {
        trackEvent('company_carousel_position_click');
      } else {
        trackEvent(eventName);
      }

      setCarouselIndex(currentIndex);
    },
    [trackEvent],
  );

  const renderContent = () => {
    if (loading) {
      return (
        <SkeletonWrapper>
          {Array.from({ length: isMobile ? MIN_ITEM_COUNT_MOBILE : MIN_ITEM_COUNT_DESKTOP }).map(
            (_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <EmployerCardLoader key={index} />
            ),
          )}
        </SkeletonWrapper>
      );
    }

    if (items?.length) {
      return (
        <Carousel
          items={items}
          itemWidthPercent={isMobile ? 80 : 25}
          hideDots={isMobile}
          onSlideChange={onSlideChange}
          ref={carouselRef}
        />
      );
    }
  };

  if (!loading && !items?.length) {
    return null;
  }

  return (
    <CarouselWrapper
      data-testid="employers-carousel"
      aria-roledescription={
        formatMessage({ id: 'comp.carousel', defaultMessage: 'Carousel' }) as string
      }
      aria-label={heading}
    >
      <CarouselHeaderWrapper>
        <CarouselHeadingContainer>
          <CarouselHeading $as={`h${headingLevel}` as const}>{heading}</CarouselHeading>
          {!!seeAllPath && (
            <LinkParagraph $as={Link} to={seeAllPath} onClick={onSeeAllClick}>
              {formatMessage({
                id: 'comp.button.see_all_employers_short',
                defaultMessage: 'See all',
              })}
            </LinkParagraph>
          )}
        </CarouselHeadingContainer>

        {!isMobile && (
          <div>
            <Button
              kind="flat"
              $style={{
                backgroundColor: theme.colors['background-global-primary'],
                paddingTop: theme.space[2],
                paddingRight: theme.space[2],
                paddingBottom: theme.space[2],
                paddingLeft: theme.space[2],
                marginRight: theme.space[8],
              }}
              disabled={carouselIndex === 0 || loading}
              onClick={() => {
                carouselRef?.current?.prevSlide?.();
              }}
              data-testid="control-button-prev"
            >
              <IconArrowLeft />
              <SrText>
                {formatMessage(
                  {
                    id: 'comp.carousel.prev_button',
                    defaultMessage: 'Back to slide {count} of {count_all}',
                  },
                  {
                    count: Math.max(1, carouselIndex),
                    count_all: slideCount,
                  },
                )}
              </SrText>
            </Button>
            <Button
              kind="flat"
              $style={{
                backgroundColor: theme.colors['background-global-primary'],
                paddingTop: theme.space[2],
                paddingRight: theme.space[2],
                paddingBottom: theme.space[2],
                paddingLeft: theme.space[2],
              }}
              disabled={carouselIndex === slideCount - 1 || loading}
              onClick={() => {
                carouselRef?.current?.nextSlide?.();
              }}
              data-testid="control-button-next"
            >
              <IconArrowRight />
              <SrText>
                {formatMessage(
                  {
                    id: 'comp.carousel.next_button',
                    defaultMessage: 'Go to slide {count} of {count_all}',
                  },
                  {
                    count: Math.min(slideCount, carouselIndex + 2),
                    count_all: slideCount,
                  },
                )}
              </SrText>
            </Button>
          </div>
        )}
      </CarouselHeaderWrapper>
      {renderContent()}
    </CarouselWrapper>
  );
};
